import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  ShoppingCart, Activity, AlertTriangle, Users, 
  TrendingUp, Package, Loader2, RefreshCw, Wrench, FileText, CheckCircle,
  Plus, ArrowRight, Calendar, FileText as FileTextIcon, Building2, Layers,
  ShoppingBag, XCircle, ClipboardList, Warehouse, FileCheck
} from 'lucide-react';
import { dashboardApiService, GeneralDashboardData } from '../services/dashboardApi';
import { inventoryReportApiService, MaterialIssueReportDto } from '../services/inventoryReportApi';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';

export function GeneralDashboard() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<GeneralDashboardData | null>(null);
  const [materialIssueData, setMaterialIssueData] = useState<Array<{ month: string; quantity: number; previousYearQuantity: number }>>([]);
  const [loadingMaterialIssue, setLoadingMaterialIssue] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadMaterialIssueData();
  }, [selectedBranch]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardApiService.getGeneralDashboard(selectedBranch || undefined);
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        toast.error(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      // console.error('Error loading general dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
    loadMaterialIssueData();
  };

  const loadMaterialIssueData = async () => {
    if (!selectedBranch) {
      return;
    }

    try {
      setLoadingMaterialIssue(true);
      const currentDate = new Date();
      
      // Calculate date range for last 24 months
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 23, 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0); // Last day of current month
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // Fetch material issue data for the date range
      const response = await inventoryReportApiService.getMaterialIssueReportByBranch(
        selectedBranch,
        startDateStr,
        endDateStr
      );

      // console.log('📊 Material Issue API Response:', {
      //   success: response.success,
      //   dataLength: response.data?.length,
      //   sampleItem: response.data?.[0],
      //   allCategoryNames: response.data?.map((item: MaterialIssueReportDto) => item.rawCategoryName).filter((v: string | undefined, i: number, arr: (string | undefined)[]) => arr.indexOf(v) === i)
      // });

      if (response.success && response.data) {
        // Filter only RAW Material category
        const rawMaterialIssues = response.data.filter(
          (issue: MaterialIssueReportDto) => {
            const categoryName = issue.rawCategoryName?.trim().toUpperCase();
            const isRawMaterial = categoryName === 'RAW MATERIAL';
            if (!isRawMaterial && categoryName) {
              // console.log('🔍 Filtered out item:', {
              //   categoryName,
              //   issueDate: issue.issueDate,
              //   issueQuantity: issue.issueQuantity,
              //   rawItemName: issue.rawItemName
              // });
            }
            return isRawMaterial;
          }
        );
        
        // console.log('📦 Filtered RAW Material issues:', {
        //   totalItems: response.data.length,
        //   rawMaterialCount: rawMaterialIssues.length,
        //   sampleRawMaterial: rawMaterialIssues[0]
        // });

        // Group by month and year, sum quantities
        const monthlyData = new Map<string, number>();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        rawMaterialIssues.forEach((issue: MaterialIssueReportDto) => {
          if (issue.issueDate && issue.issueQuantity) {
            try {
              const issueDate = new Date(issue.issueDate);
              if (isNaN(issueDate.getTime())) {
                // console.warn('⚠️ Invalid date:', issue.issueDate);
                return;
              }
              const monthKey = `${monthNames[issueDate.getMonth()]} ${issueDate.getFullYear()}`;
              
              if (!monthlyData.has(monthKey)) {
                monthlyData.set(monthKey, 0);
              }
              const quantity = Number(issue.issueQuantity) || 0;
              monthlyData.set(monthKey, monthlyData.get(monthKey)! + quantity);
              
              // console.log('✅ Processed issue:', {
              //   issueDate: issue.issueDate,
              //   parsedDate: issueDate.toISOString(),
              //   monthKey,
              //   quantity,
              //   totalForMonth: monthlyData.get(monthKey)
              // });
            } catch (error) {
              // console.error('❌ Error processing issue:', error, issue);
            }
          } else {
            // console.warn('⚠️ Missing date or quantity:', {
            //   issueDate: issue.issueDate,
            //   issueQuantity: issue.issueQuantity,
            //   hasDate: !!issue.issueDate,
            //   hasQuantity: !!issue.issueQuantity
            // });
          }
        });
        
        // console.log('📈 Monthly data map:', Array.from(monthlyData.entries()));

        // Generate data for last 12 months with previous year comparison
        const chartData: Array<{ month: string; quantity: number; previousYearQuantity: number }> = [];
        for (let i = 11; i >= 0; i--) {
          const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
          const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
          
          // Get previous year same month
          const previousYearDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth() - i, 1);
          const previousYearMonthKey = `${monthNames[previousYearDate.getMonth()]} ${previousYearDate.getFullYear()}`;
          
          chartData.push({
            month: monthKey,
            quantity: monthlyData.get(monthKey) || 0,
            previousYearQuantity: monthlyData.get(previousYearMonthKey) || 0
          });
        }

        setMaterialIssueData(chartData);
      }
    } catch (error) {
      // console.error('Error loading material issue data:', error);
      // Don't show toast error for this, as it's not critical
    } finally {
      setLoadingMaterialIssue(false);
    }
  };

  const productionLast30DaysChartData = useMemo(() => {
    const rows = dashboardData?.productionSummaryLast30Days;
    if (!Array.isArray(rows) || rows.length === 0) return [];

    return rows
      .map((r) => {
        const d = new Date(r.productionDate);
        const label = Number.isNaN(d.getTime())
          ? String(r.productionDate)
          : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
        return {
          date: label,
          sortTs: Number.isNaN(d.getTime()) ? 0 : d.getTime(),
          isSunday: !Number.isNaN(d.getTime()) ? d.getDay() === 0 : false,
          blanking: Number(r.blankingProductionQty ?? 0) || 0,
          welding: Number(r.weldingProductionQty ?? 0) || 0,
        };
      })
      .sort((a, b) => a.sortTs - b.sortTs);
  }, [dashboardData?.productionSummaryLast30Days]);

  const productionSundayByLabel = useMemo(() => {
    const m = new Map<string, boolean>();
    productionLast30DaysChartData.forEach((r: any) => {
      if (r?.date) m.set(String(r.date), Boolean(r.isSunday));
    });
    return m;
  }, [productionLast30DaysChartData]);

  const ProductionXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const isSunday = productionSundayByLabel.get(String(payload?.value ?? '')) ?? false;
    const fill = isSunday ? '#dc2626' : '#6b7280'; // red-600 else gray-500
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill={fill}
          fontSize={12}
          transform="rotate(-45)"
        >
          {payload?.value}
        </text>
      </g>
    );
  };

  const formatCurrencyValue = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyWithAbbreviation = (value: number): string => {
    if (value >= 10000000) {
      // Crore (1 Cr = 10,000,000)
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      // Lakh (1 Lk = 100,000)
      return `₹${(value / 100000).toFixed(2)} Lk`;
    } else if (value >= 1000) {
      // Thousand
      return `₹${(value / 1000).toFixed(2)} K`;
    } else {
      return formatCurrencyValue(value);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMins / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMins < 60) {
      return `${diffInMins} mins ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-red-500';
      case 'orange':
        return 'bg-orange-500';
      case 'yellow':
        return 'bg-yellow-500';
      default:
        return 'bg-orange-500';
    }
  };

  // Calculate critical alerts count
  const criticalAlertsCount = useMemo(() => {
    if (!dashboardData) return 0;
    const outOfStockCount = dashboardData.stockAlerts?.outOfStockCount ?? 0;
    const overdueCount = dashboardData.workOrderSummary?.overdueCount ?? 0;
    const heatNumberTracking = dashboardData.heatNumberTracking ?? [];
    return (
      outOfStockCount +
      overdueCount +
      (heatNumberTracking.filter(h => h.remainingQuantity < 0).length > 0 ? 1 : 0)
    );
  }, [dashboardData]);

  // Calculate material shortages count
  const materialShortagesCount = useMemo(() => {
    if (!dashboardData) return 0;
    const lowStockCount = dashboardData.stockAlerts?.lowStockCount ?? 0;
    const outOfStockCount = dashboardData.stockAlerts?.outOfStockCount ?? 0;
    return lowStockCount + outOfStockCount;
  }, [dashboardData]);

  // Calculate production efficiency
  const productionEfficiency = useMemo(() => {
    if (!dashboardData) return 0;
    const completedCount = dashboardData.workOrderSummary?.completedCount ?? 0;
    const inProgressCount = dashboardData.workOrderSummary?.inProgressCount ?? 0;
    const total = completedCount + inProgressCount;
    if (total === 0) return 0;
    return Math.round((completedCount / total) * 100);
  }, [dashboardData]);

  // Prepare critical alerts list
  const criticalAlerts = useMemo(() => {
    if (!dashboardData) return [];
    const alerts: Array<{
      type: string;
      color: string;
      message: string;
      time: string;
    }> = [];

    // Out of stock items
    (dashboardData.stockAlerts?.outOfStockItems ?? []).slice(0, 2).forEach(item => {
      alerts.push({
        type: 'Material Shortage',
        color: 'orange',
        message: `${item.itemName} - Stock below reorder level`,
        time: new Date().toISOString()
      });
    });

    // Low stock items
    (dashboardData.stockAlerts?.lowStockItems ?? []).slice(0, 1).forEach(item => {
      alerts.push({
        type: 'Material Shortage',
        color: 'orange',
        message: `${item.itemName} - Stock below reorder level`,
        time: new Date().toISOString()
      });
    });

    // Overdue work orders
    const overdueCount = dashboardData.workOrderSummary?.overdueCount ?? 0;
    if (overdueCount > 0) {
      alerts.push({
        type: 'Delayed Delivery',
        color: 'yellow',
        message: `${overdueCount} work order(s) overdue`,
        time: new Date().toISOString()
      });
    }

    // Heat numbers with negative quantity
    const negativeHeatNumbers = (dashboardData.heatNumberTracking ?? []).filter(h => h.remainingQuantity < 0);
    if (negativeHeatNumbers.length > 0) {
      alerts.push({
        type: 'Quality Issue',
        color: 'orange',
        message: `${negativeHeatNumbers.length} heat number(s) with negative quantity`,
        time: new Date().toISOString()
      });
    }

    return alerts.slice(0, 5);
  }, [dashboardData]);

  // Prepare production by work center data (mock data for now)
  const productionByWorkCenter = useMemo(() => {
    return [
      { workCenter: 'Cutting', planned: 45, actual: 42, target: 50 },
      { workCenter: 'Welding', planned: 38, actual: 35, target: 40 },
      { workCenter: 'Assembly', planned: 52, actual: 48, target: 55 },
      { workCenter: 'Quality', planned: 48, actual: 45, target: 50 },
    ];
  }, []);

  // Prepare open orders by priority data
  const openOrdersByPriority = useMemo(() => {
    if (!dashboardData) return [];
    const overdueCount = dashboardData.workOrderSummary?.overdueCount ?? 0;
    const inProgressCount = dashboardData.workOrderSummary?.inProgressCount ?? 0;
    const draftCount = dashboardData.workOrderSummary?.draftCount ?? 0;
    return [
      { name: 'Critical', value: overdueCount, color: '#ec4899' },
      { name: 'High', value: Math.max(0, inProgressCount - overdueCount), color: '#f97316' },
      { name: 'Low', value: draftCount, color: '#fbcfe8' },
    ].filter(item => item.value > 0);
  }, [dashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No data available</p>
          <Button onClick={handleRefresh} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">General Dashboard</h1>
            <p className="text-gray-600 mt-1">
              {dashboardData.lastUpdated && `Last updated: ${new Date(dashboardData.lastUpdated).toLocaleString()}`}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="border-gray-300"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI Cards - 5 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Pending Purchase Orders */}
          <Card 
            className="border-gray-200 bg-white shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => window.open('/purchase-orders?status=Draft', '_blank')}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <ShoppingBag className="h-8 w-8 text-blue-500" />
                <Badge className="bg-blue-100 text-blue-800 text-xs border-0">Pending Approval PO</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                {dashboardData.purchaseSummary.pendingPoCount}
              </p>
              <p className="text-sm text-gray-600 mb-2">Pending Purchase Orders</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('/purchase-orders/new', '_blank');
                }}
              >
                Add PO
              </Button>
            </CardContent>
          </Card>

          {/* Open Sales Orders */}
          <Card 
            className="border-gray-200 bg-white shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/sales-orders')}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <ShoppingCart className="h-8 w-8 text-green-500" />
                <Badge className="bg-green-100 text-green-800 text-xs border-0">Active</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                {dashboardData.salesOrderSummary.draftCount + dashboardData.salesOrderSummary.inProgressCount}
              </p>
              <p className="text-sm text-gray-600 mb-2">Open Sales Orders</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs border-green-200 text-green-600 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/sales-orders/new');
                }}
              >
                Add Sales Order
              </Button>
            </CardContent>
          </Card>

          {/* Ongoing/WIP Work Orders */}
          <Card 
            className="border-gray-200 bg-white shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/wip-tracking')}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500"></div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <ClipboardList className="h-8 w-8 text-purple-500" />
                <Badge className="bg-purple-100 text-purple-800 text-xs border-0">WIP</Badge>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                {dashboardData.wipSummary.totalWipCount}
              </p>
              <p className="text-sm text-gray-600 mb-2">Ongoing/WIP Work Orders</p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs border-purple-200 text-purple-600 hover:bg-purple-50"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/work-orders/new');
                }}
              >
                Create Work Order
              </Button>
            </CardContent>
          </Card>

          {/* RM Inventory */}
          <Card className="border-gray-200 bg-white shadow-sm relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500"></div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Warehouse className="h-8 w-8 text-orange-500" />
                <Badge className="bg-orange-100 text-orange-800 text-xs border-0">Stock</Badge>
              </div>
              {(() => {
                if (!dashboardData.rawMaterialCategoryInventory) {
                  return (
                    <>
                      <p className="text-3xl font-bold text-gray-900 mb-1">0</p>
                      <p className="text-sm text-gray-600 mb-2">RM Inventory</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-orange-200 text-orange-600 hover:bg-orange-50"
                        onClick={() => window.open('/grn/new', '_blank')}
                      >
                        Create GRN
                      </Button>
                    </>
                  );
                }
                const rawMaterialItem = dashboardData.rawMaterialCategoryInventory.find(
                  item => item.categoryName?.trim().toUpperCase() === 'RAW MATERIAL'
                );
                const rawMaterialQuantity = rawMaterialItem?.quantity || 0;
                const rawMaterialValue = rawMaterialItem?.value || 0;
                
                return (
                  <>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                      {rawMaterialQuantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Ton
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        ({formatCurrencyWithAbbreviation(rawMaterialValue)})
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 mb-2">RM Inventory</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs border-orange-200 text-orange-600 hover:bg-orange-50"
                      onClick={() => window.open('/grn/new', '_blank')}
                    >
                      Create GRN
                    </Button>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* RM Issue */}
          <Card className="border-gray-200 bg-white shadow-sm relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-500"></div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Package className="h-8 w-8 text-cyan-500" />
                <Badge className="bg-cyan-100 text-cyan-800 text-xs border-0">Issued</Badge>
              </div>
              {(() => {
                if (!dashboardData.rawMaterialCategoryIssue) {
                  return (
                    <>
                      <p className="text-3xl font-bold text-gray-900 mb-1">0</p>
                      <p className="text-sm text-gray-600">RM Issue</p>
                    </>
                  );
                }
                const rawMaterialItem = dashboardData.rawMaterialCategoryIssue.find(
                  item => item.categoryName?.trim().toUpperCase() === 'RAW MATERIAL'
                );
                const rawMaterialIssueQuantity = rawMaterialItem?.issueQuantity || 0;
                
                // Get last month's issue quantity
                let lastMonthQuantity = 0;
                if (dashboardData.rawMaterialCategoryIssueLastMonth) {
                  const lastMonthItem = dashboardData.rawMaterialCategoryIssueLastMonth.find(
                    item => item.categoryName?.trim().toUpperCase() === 'RAW MATERIAL'
                  );
                  lastMonthQuantity = lastMonthItem?.issueQuantity || 0;
                }
                
                return (
                  <>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                      {rawMaterialIssueQuantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Ton
                    </p>
                    <p className="text-sm text-gray-600">
                      RM Issue
                      {lastMonthQuantity > 0 && (
                        <span className="block text-xs text-gray-500 mt-1">
                          Last Month: {lastMonthQuantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Ton
                        </span>
                      )}
                    </p>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Material Issue Chart - RAW Material */}
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Material Issue - RAW Material (Last 12 Months)</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open('/reports/material-issue', '_blank')}
              >
                Full report <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingMaterialIssue ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : materialIssueData.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No material issue data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={materialIssueData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    label={{ value: 'Quantity (Ton)', angle: -90, position: 'insideLeft' }}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string) => [`${formatNumber(value)} Ton`, name]}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend />
                  <Bar dataKey="quantity" fill="#FF7A5C" name="Current Year" radius={[4, 4, 0, 0]}>
                    <LabelList 
                      dataKey="quantity" 
                      position="top" 
                      formatter={(value: number) => formatNumber(value)}
                      style={{ fontSize: '11px', fill: '#6b7280' }}
                    />
                  </Bar>
                  <Bar dataKey="previousYearQuantity" fill="#94a3b8" name="Previous Year" radius={[4, 4, 0, 0]}>
                    <LabelList 
                      dataKey="previousYearQuantity" 
                      position="top" 
                      formatter={(value: number) => formatNumber(value)}
                      style={{ fontSize: '11px', fill: '#6b7280' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Production Summary Chart - Last 30 Days */}
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Production Summary (Last 30 Days)</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open('/reports/production', '_blank')}
              >
                Full report <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {productionLast30DaysChartData.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No production summary data available</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={productionLast30DaysChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={<ProductionXAxisTick />}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      return [formatNumber(value), String(name)];
                    }}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend />
                  <Bar dataKey="blanking" fill="#3b82f6" name="Blanking" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="welding" fill="#f97316" name="Welding" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
